/**
 * UI 테마 Pinia store (R.19).
 *
 * - appearance: light | dark  → html.dark class 전환 (EP dark CSS vars 활성화)
 * - fontSize: small | normal | large → data-ax-font-size attribute (CSS token 전환)
 * - colorPreset: 프리셋 키 → --el-color-primary / --ax-color-primary 직접 설정
 *
 * 모든 설정은 localStorage('ax-ui-theme')에 자동 저장·복원.
 * BrandingConfig 는 제품 기본값, 이 store 는 사용자 로컬 오버라이드.
 * createAxApp 에서 app.use(pinia) 이후 init() 를 호출해 DOM 에 즉시 반영.
 */

import { defineStore } from 'pinia';
import { ref, watch } from 'vue';

import {
  AX_COLOR_PRESETS,
  AX_TIER_DEFAULT_COLOR_PRESET,
  type ColorPresetDef,
} from '../branding/color-presets';

export type AppearanceMode = 'light' | 'dark';
export type FontSizeMode = 'small' | 'normal' | 'large';

export type { ColorPresetDef };
export { AX_COLOR_PRESETS, AX_TIER_DEFAULT_COLOR_PRESET };

const STORAGE_KEY = 'ax-ui-theme';

/** createAxApp 부팅 전 configureUiThemeDefaults 로 설정 (기본: core → indigo) */
let configuredDefaultPreset: string = AX_TIER_DEFAULT_COLOR_PRESET.core;

export function configureUiThemeDefaults(presetKey: string): void {
  configuredDefaultPreset = presetKey;
}

interface PersistedState {
  appearance: AppearanceMode;
  fontSize: FontSizeMode;
  colorPreset: string;
}

function loadPersistedState(): PersistedState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<PersistedState>;
      return {
        appearance: parsed.appearance ?? 'light',
        fontSize: parsed.fontSize ?? 'normal',
        colorPreset: parsed.colorPreset ?? configuredDefaultPreset,
      };
    }
  } catch {
    // storage unavailable — fall through
  }
  return { appearance: 'light', fontSize: 'normal', colorPreset: configuredDefaultPreset };
}

function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace('#', '');
  return [
    parseInt(clean.slice(0, 2), 16),
    parseInt(clean.slice(2, 4), 16),
    parseInt(clean.slice(4, 6), 16),
  ];
}

function toHex(r: number, g: number, b: number): string {
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

function mixColor(hex: string, mixHex: string, weight: number): string {
  const [r1, g1, b1] = hexToRgb(hex);
  const [r2, g2, b2] = hexToRgb(mixHex);
  return toHex(
    Math.round(r1 * (1 - weight) + r2 * weight),
    Math.round(g1 * (1 - weight) + g2 * weight),
    Math.round(b1 * (1 - weight) + b2 * weight),
  );
}

function applyPrimaryColor(primary: string): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.style.setProperty('--el-color-primary', primary);
  root.style.setProperty('--ax-color-primary', primary);
  // EP light variants: mix primary with white at n * 10%
  for (let n = 1; n <= 9; n++) {
    root.style.setProperty(`--el-color-primary-light-${n}`, mixColor(primary, '#ffffff', n * 0.1));
  }
  // EP dark variant: mix primary with black at 20%
  root.style.setProperty('--el-color-primary-dark-2', mixColor(primary, '#000000', 0.2));
}

function applyFontSize(size: FontSizeMode): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-ax-font-size', size);
}

function applyAppearance(mode: AppearanceMode): void {
  if (typeof document === 'undefined') return;
  if (mode === 'dark') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
}

export const useUiThemeStore = defineStore('uiTheme', () => {
  const saved = loadPersistedState();
  const appearance = ref<AppearanceMode>(saved.appearance);
  const fontSize = ref<FontSizeMode>(saved.fontSize);
  const colorPreset = ref<string>(saved.colorPreset);

  function currentPresetColor(): string {
    return (
      AX_COLOR_PRESETS.find((p) => p.key === colorPreset.value)?.primary ??
      AX_COLOR_PRESETS[0]!.primary
    );
  }

  function applyAll(): void {
    applyPrimaryColor(currentPresetColor());
    applyFontSize(fontSize.value);
    applyAppearance(appearance.value);
  }

  function persist(): void {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          appearance: appearance.value,
          fontSize: fontSize.value,
          colorPreset: colorPreset.value,
        }),
      );
    } catch {
      // storage might be unavailable
    }
  }

  watch([appearance, fontSize, colorPreset], () => {
    applyAll();
    persist();
  });

  /** 앱 부팅 시 한 번 호출해 저장된 테마를 DOM에 즉시 반영한다. */
  function init(): void {
    applyAll();
  }

  return {
    appearance,
    fontSize,
    colorPreset,
    presets: AX_COLOR_PRESETS,
    init,
  };
});
